import React, { useEffect, useMemo, useState } from 'react';
import { Auth0Context, Auth0ContextInterface, initialContext } from '@auth0/auth0-react';
import { MyAuth0Context } from 'index';

declare global {
  interface Window {
    __E2E__?: {
      token?: string;
      jwksMockUrl?: string;
      user?: Record<string, unknown>;
    };
  }
}

const DEFAULT_JWKS_URL = 'http://127.0.0.1:4000';

const E2E_USER = {
  sub: 'auth0|test-user-001',
  email: 'testuser@e2e.test',
  name: 'Test User',
  nickname: 'testuser',
  picture: '',
  email_verified: true,
  updated_at: new Date().toISOString()
};

/**
 * Drop-in replacement for Auth0Provider during E2E tests.
 *
 * Two modes of operation:
 * 1. Playwright injects `window.__E2E__ = { token }` via addInitScript — used immediately.
 * 2. Docker sets `window.__E2E__ = { jwksMockUrl }` via env.js — the component
 *    fetches a JWT from the JWKS mock server on mount.
 * @param {{children: React.ReactNode}} root0 - Component props.
 * @param {React.ReactNode} root0.children - Nested components to render.
 * @returns {JSX.Element} Wrapped provider tree with mocked Auth0 context.
 */
export const MockAuth0Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const e2e = window.__E2E__;
  const injectedToken = e2e?.token;
  const jwksMockUrl = e2e?.jwksMockUrl || DEFAULT_JWKS_URL;
  const user = { ...E2E_USER, ...(e2e?.user ?? {}) };

  const [token, setToken] = useState<string | null>(injectedToken ?? null);
  const [loading, setLoading] = useState(!injectedToken);

  useEffect(() => {
    if (injectedToken) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${jwksMockUrl}/api/e2e/mint-token?sub=${encodeURIComponent(user.sub)}`);
        if (!res.ok) throw new Error(`mint-token failed: ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setToken(data.token);
          setLoading(false);
        }
      } catch (err) {
        console.error('[MockAuth0Provider] Failed to fetch token from JWKS mock:', err);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo<Auth0ContextInterface>(() => {
    return {
      ...initialContext,
      isAuthenticated: !!token,
      isLoading: loading,
      user,
      getAccessTokenSilently: (async () => token ?? '') as Auth0ContextInterface['getAccessTokenSilently'],
      getAccessTokenWithPopup: (async () => token ?? '') as Auth0ContextInterface['getAccessTokenWithPopup'],
      getIdTokenClaims: (async () => undefined) as Auth0ContextInterface['getIdTokenClaims'],
      loginWithRedirect: (async () => undefined) as Auth0ContextInterface['loginWithRedirect'],
      loginWithPopup: (async () => undefined) as Auth0ContextInterface['loginWithPopup'],
      logout: (async () => {
        window.location.href = '/';
      }) as Auth0ContextInterface['logout'],
      handleRedirectCallback: (async () => ({ appState: {} })) as Auth0ContextInterface['handleRedirectCallback']
    };
  }, [token, loading, user]);

  return (
    <Auth0Context.Provider value={contextValue}>
      <MyAuth0Context.Provider value={contextValue}>{children}</MyAuth0Context.Provider>
    </Auth0Context.Provider>
  );
};
